import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import "./styles/app.css";

import ConfirmDialog from "./components/common/ConfirmDialog";
import LoadingSkeleton from "./components/common/LoadingSkeleton";
import OnboardingDialog from "./components/common/OnboardingDialog";
import ExpenseForm from "./components/expenses/ExpenseForm";
import GoalDialog from "./components/GoalDialog";
import Notifications from "./components/Notifications";
import Shell from "./components/layout/Shell";
import { emptyFilters } from "./constants";
import { ThemeProvider } from "./contexts/ThemeContext";
import useAuth from "./hooks/useAuth";
import useConfirmDialog from "./hooks/useConfirmDialog";
import useExpenses from "./hooks/useExpenses";
import useGoals from "./hooks/useGoals";
import useLocalStorage from "./hooks/useLocalStorage";
import useNotifications from "./hooks/useNotifications";
import usePwaInstall from "./hooks/usePwaInstall";
import useSystemTheme from "./hooks/useSystemTheme";
import { getLocale } from "./locales";
import AuthPage from "./pages/AuthPage";
import ExpensesPage from "./pages/ExpensesPage";
import GoalsPage from "./pages/GoalsPage";
import ProfilePage from "./pages/ProfilePage";
import SettingsPage from "./pages/SettingsPage";
import { viewIds } from "./routes/views";
import { storageService } from "./services/storageService";
import { getThemeName } from "./theme";
import { downloadJson, readFileAsText } from "./utils/formatters";

const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));

function App() {
  const [filters, setFilters] = useState(emptyFilters);
  const [activeView, setActiveView] = useLocalStorage("ledgerflow_active_view", viewIds.dashboard);
  const [drawerCollapsed, setDrawerCollapsed] = useLocalStorage("ledgerflow_drawer_collapsed", false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingGoal, setEditingGoal] = useState(null);
  const [isExpenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [isGoalDialogOpen, setGoalDialogOpen] = useState(false);
  const [isOnboardingOpen, setOnboardingOpen] = useState(false);
  const [isAppLoading, setAppLoading] = useState(false);
  const systemDark = useSystemTheme();
  const { closeNotification, notifications, showNotification } = useNotifications();
  const { canInstall, installApp } = usePwaInstall();

  const handleAuthenticated = useCallback(() => {
    setActiveView(viewIds.dashboard);
  }, [setActiveView]);

  const handleAuthLogout = useCallback(() => {
    setFilters(emptyFilters);
    setActiveView(viewIds.dashboard);
  }, [setActiveView]);

  const {
    currentUser,
    isAuthLoading,
    login,
    logout,
    register,
    updateUser
  } = useAuth({
    onAuthenticated: handleAuthenticated,
    onLogout: handleAuthLogout,
    showNotification
  });

  const locale = useMemo(() => getLocale(currentUser?.language), [currentUser?.language]);
  const notificationsText = locale.notifications;
  const dialogsText = locale.dialogs;
  const { closeConfirm, confirmDialog, requestConfirm, runConfirmedAction } = useConfirmDialog({
    onError: (message) => showNotification("error", message),
    t: notificationsText
  });
  const expenseState = useExpenses(currentUser, filters, { locale, showNotification });
  const goalState = useGoals(currentUser, { locale, showNotification });

  const currencyCode = currentUser?.currency?.value || "USD";
  const monthlyBudget = Number(currentUser?.monthlyBudget || 0);
  const monthlyIncome = Number(currentUser?.monthlyIncome || 0);
  const themeMode = currentUser?.themeMode || (currentUser?.darkMode ? "dark" : "auto");
  const effectiveTheme = getThemeName(themeMode, systemDark);
  const fontSizeValue = currentUser?.fontSize?.value || currentUser?.fontSize || "comfortable";
  const accentColor = currentUser?.accentColor || "#2563eb";
  const isInitialLoading =
    (expenseState.isLoading || goalState.isLoading) &&
    !expenseState.expenses.length &&
    !goalState.goals.length;
  const isBusy = isAppLoading || isAuthLoading || expenseState.isLoading || goalState.isLoading;

  useEffect(() => {
    if (!currentUser?.id) {
      setOnboardingOpen(false);
      return;
    }

    const onboardingKey = `ledgerflow_onboarding_seen_${currentUser.id}`;
    setOnboardingOpen(!localStorage.getItem(onboardingKey));
  }, [currentUser?.id]);

  useEffect(() => {
    document.documentElement.dataset.theme = effectiveTheme;
    document.documentElement.style.colorScheme = effectiveTheme;
  }, [effectiveTheme]);

  const openExpenseDialog = (expense = null) => {
    setEditingExpense(expense);
    setExpenseDialogOpen(true);
  };

  const closeExpenseDialog = () => {
    setEditingExpense(null);
    setExpenseDialogOpen(false);
  };

  const saveExpense = async (draft) => {
    await expenseState.saveExpense(draft, editingExpense);
    closeExpenseDialog();
  };

  const requestDeleteExpense = (expenseId) => {
    requestConfirm({
      ...dialogsText.deleteExpense,
      onConfirm: () => expenseState.deleteExpense(expenseId)
    });
  };

  const openGoalDialog = (goal = null) => {
    setEditingGoal(goal);
    setGoalDialogOpen(true);
  };

  const closeGoalDialog = () => {
    setEditingGoal(null);
    setGoalDialogOpen(false);
  };

  const saveGoal = async (draft) => {
    await goalState.saveGoal(draft, editingGoal);
    closeGoalDialog();
  };

  const requestDeleteGoal = (goalId) => {
    requestConfirm({
      ...dialogsText.deleteGoal,
      onConfirm: () => goalState.deleteGoal(goalId)
    });
  };

  const persistSettings = async (settings) => {
    setAppLoading(true);

    try {
      const user = await storageService.updateUserSettings(currentUser.id, {
        ...settings,
        darkMode: settings.themeMode === "dark"
      });
      updateUser(user);
      showNotification("success", notificationsText.settingsSaved);
    } catch (error) {
      showNotification("error", error.message || notificationsText.settingsSaveFailed);
    } finally {
      setAppLoading(false);
    }
  };

  const saveSettings = (settings) => {
    if (settings.monthlyBudget > 0 && settings.monthlyBudget < expenseState.total) {
      requestConfirm({
        ...dialogsText.saveBudget,
        onConfirm: () => persistSettings(settings)
      });
      return;
    }

    persistSettings(settings);
  };

  const previewThemeMode = (nextThemeMode) => {
    updateUser((user) => ({
      ...user,
      themeMode: nextThemeMode,
      darkMode: nextThemeMode === "dark"
    }));
  };

  const previewLanguage = (nextLanguage) => {
    updateUser((user) => ({
      ...user,
      language: nextLanguage
    }));
  };

  const saveThemeMode = async (nextThemeMode) => {
    previewThemeMode(nextThemeMode);

    try {
      const savedUser = await storageService.updateUserSettings(currentUser.id, {
        themeMode: nextThemeMode,
        darkMode: nextThemeMode === "dark"
      });
      updateUser(savedUser);
    } catch (error) {
      showNotification("error", notificationsText.themeSaveFailed);
    }
  };

  const saveProfile = async (profile) => {
    if (!profile.fullName.trim() || !profile.email.trim()) {
      showNotification("error", notificationsText.profileRequired);
      return;
    }

    setAppLoading(true);

    try {
      const user = await storageService.updateUserSettings(currentUser.id, profile);
      updateUser(user);
      showNotification("success", notificationsText.profileSaved);
    } catch (error) {
      showNotification("error", error.message || notificationsText.profileSaveFailed);
    } finally {
      setAppLoading(false);
    }
  };

  const completeOnboarding = () => {
    if (currentUser?.id) {
      localStorage.setItem(`ledgerflow_onboarding_seen_${currentUser.id}`, "true");
    }

    setOnboardingOpen(false);
  };

  const requestInstallApp = async () => {
    const accepted = await installApp();
    showNotification(
      accepted ? "success" : "info",
      accepted
        ? notificationsText.appInstalled || "LedgerFlow is ready to open as an app."
        : notificationsText.installDismissed || "Install skipped. You can install LedgerFlow later."
    );
  };

  const downloadBackup = () => {
    const backup = storageService.createBackup(currentUser, expenseState.expenses, goalState.goals);
    downloadJson(backup, `ledgerflow-backup-${new Date().toISOString().slice(0, 10)}.json`);
    showNotification("success", notificationsText.backupDownloaded);
  };

  const restoreBackup = async (file) => {
    setAppLoading(true);

    try {
      const text = await readFileAsText(file);
      const backup = JSON.parse(text);
      const restored = await storageService.restoreBackup(currentUser.id, currentUser, backup);
      updateUser(restored.user);
      expenseState.replaceExpenses(restored.expenses);
      goalState.replaceGoals(restored.goals);
      showNotification("success", notificationsText.backupRestored);
    } catch (error) {
      showNotification("error", error.message || notificationsText.backupRestoreFailed);
    } finally {
      setAppLoading(false);
    }
  };

  const requestRestoreBackup = (file) => {
    requestConfirm({
      ...dialogsText.restoreBackup,
      onConfirm: () => restoreBackup(file)
    });
  };

  const requestResetAllData = () => {
    requestConfirm({
      ...dialogsText.resetWorkspace,
      onConfirm: async () => {
        await Promise.all([expenseState.resetExpenses(), goalState.resetGoals()]);
        showNotification("success", notificationsText.localDataReset);
      }
    });
  };

  const renderView = () => {
    if (isInitialLoading) {
      return <LoadingSkeleton />;
    }

    if (activeView === viewIds.expenses) {
      return (
        <ExpensesPage
          currencyCode={currencyCode}
          expenses={expenseState.displayedExpenses}
          filters={filters}
          locale={locale}
          onAdd={() => openExpenseDialog()}
          onDelete={requestDeleteExpense}
          onEdit={openExpenseDialog}
          onExportCsv={expenseState.exportCsv}
          onExportJson={expenseState.exportJson}
          onImportFile={expenseState.importExpenses}
          onResetAll={requestResetAllData}
          onUpdateFilters={setFilters}
          t={locale.expenses}
        />
      );
    }

    if (activeView === viewIds.goals) {
      return (
        <GoalsPage
          currencyCode={currencyCode}
          goals={goalState.goalProgress}
          locale={locale}
          onAdd={() => openGoalDialog()}
          onDelete={requestDeleteGoal}
          onEdit={openGoalDialog}
          onReorder={goalState.reorderGoals}
          t={locale.goals}
        />
      );
    }

    if (activeView === viewIds.reports) {
      return (
        <ReportsPage
          categoryData={expenseState.categoryData}
          currencyCode={currencyCode}
          expenses={expenseState.currentMonthExpenses}
          insights={expenseState.insights}
          locale={locale}
          monthlyData={expenseState.monthlyData}
          t={locale.reports}
        />
      );
    }

    if (activeView === viewIds.profile) {
      return (
        <ProfilePage
          currentUser={currentUser}
          isLoading={isBusy}
          locale={locale}
          onSave={saveProfile}
          t={locale.profile}
        />
      );
    }

    if (activeView === viewIds.settings) {
      return (
        <SettingsPage
          currentUser={currentUser}
          locale={locale}
          onBackup={downloadBackup}
          onPreviewLanguage={previewLanguage}
          onPreviewThemeMode={previewThemeMode}
          onResetAll={requestResetAllData}
          onRestore={requestRestoreBackup}
          onSave={saveSettings}
          t={locale.settings}
        />
      );
    }

    return (
      <DashboardPage
        balance={expenseState.balance}
        budget={monthlyBudget}
        budgetPercentage={expenseState.budgetPercentage}
        categoryBudgetData={expenseState.categoryBudgetData}
        categoryData={expenseState.categoryData}
        currencyCode={currencyCode}
        goalCount={goalState.goals.length}
        goals={goalState.goalProgress}
        insights={expenseState.insights}
        locale={locale}
        monthlyData={expenseState.monthlyData}
        monthlyIncome={monthlyIncome}
        onAddExpense={() => openExpenseDialog()}
        onOpenSettings={() => setActiveView(viewIds.settings)}
        recentExpenses={expenseState.recentExpenses}
        recurringCount={expenseState.recurringCount}
        remainingBudget={expenseState.remainingBudget}
        t={locale.dashboard}
        topCategory={expenseState.categoryData[0]}
        total={expenseState.total}
        transactionCount={expenseState.currentMonthExpenses.length}
      />
    );
  };

  if (!currentUser) {
    return (
      <div className="app" data-theme="light">
        <AuthPage isLoading={isAuthLoading} onLogin={login} onRegister={register} t={locale.auth} />
        <Notifications notifications={notifications} onClose={closeNotification} />
      </div>
    );
  }

  return (
    <ThemeProvider value={{ effectiveTheme, themeMode }}>
      <div
        className="app"
        data-font-size={fontSizeValue}
        data-theme={effectiveTheme}
        style={{ "--user-accent": accentColor }}
      >
        <Shell
          activeView={activeView}
          currentUser={currentUser}
          drawerCollapsed={drawerCollapsed}
          effectiveTheme={effectiveTheme}
          locale={locale}
          onLogout={logout}
          onInstallApp={canInstall ? requestInstallApp : null}
          onThemeModeChange={saveThemeMode}
          onToggleDrawer={() => setDrawerCollapsed((value) => !value)}
          onViewChange={setActiveView}
          themeMode={themeMode}
          t={locale.shell}
        >
          <Suspense fallback={<LoadingSkeleton />}>
            {renderView()}
          </Suspense>
        </Shell>

        {isExpenseDialogOpen && (
          <ExpenseForm
            currencyCode={currencyCode}
            expense={editingExpense}
            isLoading={isBusy}
            locale={locale}
            onClose={closeExpenseDialog}
            onSave={saveExpense}
            onValidationError={(message) => showNotification("error", message)}
            t={locale.expenseForm}
          />
        )}

        {isGoalDialogOpen && (
          <GoalDialog
            goal={editingGoal}
            isLoading={isBusy}
            onClose={closeGoalDialog}
            onSave={saveGoal}
            onValidationError={(message) => showNotification("error", message)}
            t={locale.goalForm}
          />
        )}

        {isOnboardingOpen && (
          <OnboardingDialog onClose={completeOnboarding} t={locale.onboarding} />
        )}

        <ConfirmDialog
          cancelLabel={locale.common.cancel}
          confirmLabel={confirmDialog?.confirmLabel}
          isOpen={Boolean(confirmDialog)}
          message={confirmDialog?.message}
          onCancel={closeConfirm}
          onConfirm={runConfirmedAction}
          title={confirmDialog?.title}
        />
        <Notifications notifications={notifications} onClose={closeNotification} />
      </div>
    </ThemeProvider>
  );
}

export default App;
